import React, { useContext, useState, useEffect } from "react";
import { Form as FinalForm, Field } from "react-final-form";
import { Container, Form, Button } from "semantic-ui-react";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import { observer } from "mobx-react-lite";
import {
  combineValidators,
  isRequired,
  composeValidators,
  hasLengthLessThan,
  matchesPattern,
  isAlphaNumeric,
  hasLengthGreaterThan,
} from "revalidate";
import TextInput from "../../../../app/common/form/TextInput";
import { documentsType } from "../../dian/constants"

const validate = combineValidators({
  softwareId: composeValidators(
    isRequired({ message: "Campo obligatorio" }),
    matchesPattern(/^[0-9a-fA-F]{8}[-]([0-9a-fA-F]{4}[-]){3}[0-9a-fA-F]{12}$/)({
      message: "Formato esperado: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    }),
    hasLengthLessThan(37)({
      message: "El ID Software debe tener máximo 36 caracteres",
    }),
    hasLengthGreaterThan(35)({
      message: "El ID Software debe tener al menos 36 caracteres",
    }),
  )(),
  softwarePin: composeValidators(
    isRequired({ message: "Campo obligatorio" }),
    isAlphaNumeric({ message: "El PIN debe ser alfanumérico" }),
    hasLengthLessThan(6)({
      message: "El PIN debe tener máximo 5 caracteres",
    }),
    hasLengthGreaterThan(4)({
      message: "El PIN debe tener al menos 5 caracteres",
    })
  )(),
  payrollSoftwareId: (value: string, allValues: any) => {
    if (!value && !allValues.payrollSoftwarePin) {
      return undefined; // Ambos vacíos está bien
    }
    if (!value && allValues.payrollSoftwarePin) {
      return "Si ingresa el PIN de nómina, también debe ingresar el ID del software";
    }
    if (value && !allValues.payrollSoftwarePin) {
      return "Si ingresa el ID del software de nómina, también debe ingresar el PIN";
    }
    // Si ambos están llenos, validar formato
    const formatValidation = matchesPattern(/^[0-9a-fA-F]{8}[-]([0-9a-fA-F]{4}[-]){3}[0-9a-fA-F]{12}$/)({ 
      message: "Formato esperado: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    })(value);
    if (formatValidation) return formatValidation;
    
    const lengthValidation = hasLengthLessThan(37)({
      message: "El ID Software debe tener máximo 36 caracteres",
    })(value);
    return lengthValidation;
  },
  payrollSoftwarePin: (value: string, allValues: any) => {
    if (!value && !allValues.payrollSoftwareId) {
      return undefined; // Ambos vacíos está bien
    }
    if (!value && allValues.payrollSoftwareId) {
      return "Si ingresa el ID del software de nómina, también debe ingresar el PIN";
    }
    if (value && !allValues.payrollSoftwareId) {
      return "Si ingresa el PIN de nómina, también debe ingresar el ID del software";
    }
    // Si ambos están llenos, validar formato
    const alphaNumericValidation = isAlphaNumeric({ message: "El PIN debe ser alfanumérico" })(value);
    if (alphaNumericValidation) return alphaNumericValidation;
    
    const lengthValidation = hasLengthLessThan(6)({
      message: "El PIN debe tener máximo 5 caracteres",
    })(value);
    return lengthValidation;
  },
});

const getSendDocumentsStatusLabels = (sendStatus: number) => {
  switch (sendStatus) {
    case 1:
      return "En ambiente productivo";
    default:
      return "En proceso de habilitación";
  }
};

const SwDian = () => {
  //variables para uso la informacion del userStore
  const rootStore = useContext(RootStoreContext);
  const {
    supplier,
    btSupplierSw,
    updateSupplierSoftware,
    getTestSetDocuments,
    testSetDocuments,
  } = rootStore.companyStore;

  const [tributaryForm] = useState({
    softwareId: supplier.softwareId || "",
    softwarePin: supplier.softwarePin || "",
    softwareName: supplier.softwareName || "",
    softwareCreatorName: supplier.softwareCreatorName || "",
    payrollSoftwareId: supplier.payrollSoftwareId || "",
    payrollSoftwarePin: supplier.payrollSoftwarePin || ""
  });

  useEffect(() => {
    getTestSetDocuments();
  }, [getTestSetDocuments]);

  const documentsState = JSON.parse(JSON.stringify(testSetDocuments));


  if (!testSetDocuments) {
    return null;
  }

  const handleFinalFormSubmit = (values: any) => {
    updateSupplierSoftware(values);
  };

  return (
    <div className="tabContentStyle">
      <FinalForm
        validate={validate}
        initialValues={tributaryForm}
        onSubmit={handleFinalFormSubmit}
        render={({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Container textAlign="justified">
              <h4 className="ui header" style={{ color: "#2185d0" }}>
                <i className="building outline icon"></i>
                <div className="content">
                  Información General del Software
                  <div className="sub header">Datos del fabricante y software utilizado</div>
                </div>
              </h4>
              
              <label>Nombre o razon social del fabricante del software</label>
              <Field name="softwareCreatorName" component={TextInput} />
              <label>Nombre del software</label>
              <Field name="softwareName" component={TextInput} />
              
              <div className="ui divider"></div>
              <h4 className="ui header" style={{ color: "#2185d0" }}>
                <i className="file invoice outline icon"></i>
                <div className="content">
                  Facturación Electrónica
                  <div className="sub header">Configuración para documentos electrónicos</div>
                </div>
              </h4>
              
              <label>Identificación Software</label>
              <Field name="softwareId" component={TextInput} />
              <label>Pin Software</label>
              <Field name="softwarePin" component={TextInput} />
              
              <div className="ui divider"></div>
              <h4 className="ui header" style={{ color: "#2185d0" }}>
                <i className="money bill alternate outline icon"></i>
                <div className="content">
                  Nómina Electrónica
                  <div className="sub header">Configuración para documentos de nómina electrónica</div>
                </div>
              </h4>
              
              <label>Identificación Software para Nómina</label>
              <Field name="payrollSoftwareId" component={TextInput} />
              <label>Pin Software para Nómina</label>
              <Field name="payrollSoftwarePin" component={TextInput} />
              
              <div className="ui divider"></div>
              <h4 className="ui header" style={{ color: "#2185d0" }}>
                <i className="check circle outline icon"></i>
                <div className="content">
                  Estado de Habilitación
                  <div className="sub header">Estados actuales de los tipos de documentos</div>
                </div>
              </h4>
              
              <div className="document-state__row">
                <label>Estado Factura electrónica</label>
                <p className="readonlyfield">{getSendDocumentsStatusLabels(documentsState?.[`${documentsType.invoiceSale}`]?.sendState)}</p>
              </div>
              <div className="document-state__row">
                <label>Estado Documento equivalente</label>
                <p className="readonlyfield">{getSendDocumentsStatusLabels(documentsState?.[`${documentsType.equivalentDocumentSPD}`]?.sendState)}</p>
              </div>
              <div className="document-state__row">
                <label>Estado Documento soporte</label>
                <p className="readonlyfield">{getSendDocumentsStatusLabels(documentsState?.[`${documentsType.supportDocument}`]?.sendState)}</p>
              </div>
              <div className="document-state__row">
                <label>Estado Nómina electrónica</label>
                <p className="readonlyfield">{getSendDocumentsStatusLabels(documentsState?.[`${documentsType.payroll}`]?.sendState)}</p>
              </div>
              
              <div className="ui divider"></div>
              <Button
                disabled={btSupplierSw}
                loading={btSupplierSw}
                className="ui primary button"
                color="blue"
                type="submit"
                content="Guardar"
              />
            </Container>
          </Form>
        )}
      />
    </div>
  );
};

export default observer(SwDian);
